import { useAxios } from "./useAxios"
import { Pagination } from "@/interfaces/Pagination"
import { BotStatusEnum } from "@/enums/bots/BotStatusEnum"
import { Bot } from "@/interfaces/Bot"
import { ref } from "vue"
import Cookies from "js-cookie";
import { BotTypeEnum } from "@/enums/bots/BotTypeEnum"
import { BotCommand } from "@/interfaces/BotCommand"

const axios = useAxios()

export interface CreateBotDTO {
  name?: string;
  token: string;
  backupToken?: string;
  type: BotTypeEnum;
  startMessage?: string;
  paymentPlansMessage?: string;
  actionButtonLabel?: string;
  generatingPixMessage?: string;
  pixGeneratedInstruction?: string;
  pixPaymentInstruction?: string;
  pixPaymentStatusMessage?: string;
  pixVerifyButtonLabel?: string;
  status: BotStatusEnum;
  startMedia: File
  topMedia: File
  referringOrganizationUUID?: string
  displayPixQrCode?: boolean
  allowSubscriptionsCommand?: boolean
  cloakerEnabled?: boolean
  commands?: BotCommand[]
  audios?: File[]
  topMediaHasSpoiler?: boolean;
  profileName?: string;
  profileDescription?: string;
  profileAbout?: string;
  profilePic?: File;
}


export interface UpdateBotDTO {
  name?: string;
  token?: string;
  startMessage?: string;
  paymentPlansMessage?: string;
  actionButtonLabel?: string;
  generatingPixMessage?: string;
  pixGeneratedInstruction?: string;
  pixPaymentInstruction?: string;
  pixPaymentStatusMessage?: string;
  pixVerifyButtonLabel?: string;
  status?: BotStatusEnum;
  displayPixQrCode?: boolean
  allowSubscriptionsCommand?: boolean
  cloakerEnabled?: boolean
  commands?: BotCommand[]
  deletedMediaIds?: number[]
  startMediaHasSpoiler?: boolean;
  topMediaHasSpoiler?: boolean;
  profileName?: string;
  profileDescription?: string;
  profileAbout?: string;
  profilePic?: File;
}


export interface GetAllBotsParams {
  page?: string
  perPage?: string
}

export function useBot() {
  const organizationId = Cookies.get("organization") ? JSON.parse(Cookies.get("organization") ?? "null")?.id : process.env.VUE_APP_ORGANIZATION_ID;
  const selectedBot = ref<Bot | null>(localStorage.getItem('selectedBot') ? JSON.parse(localStorage.getItem('selectedBot') as string) : null)
  const selectableBots = ref<Bot[]>([])
  const botLink = selectedBot.value ? (selectedBot.value?.backupToken ? `https://bot.mybotexpress.com/${selectedBot.value?.uuid}?start=` : `https://t.me/${selectedBot.value?.name}?start=`) : null

  const create = async (payload: CreateBotDTO): Promise<Bot> => {
    const formData = new FormData()

    for (const key in payload) {
      if (key === 'commands') {
        formData.append('commands', JSON.stringify(payload.commands ?? []))
      } else if (payload[key as keyof typeof payload] !== null && payload[key as keyof typeof payload] !== '' && !Array.isArray(payload[key as keyof typeof payload])) {
        formData.append(key, payload[key as keyof typeof payload] as string | File)
      } else if (Array.isArray(payload[key as keyof typeof payload])) {
        for (const item of payload[key as keyof typeof payload] as any[]) {
          if (item instanceof File) {
            formData.append(key, item)
          } else if (!!item) {
            formData.append(`${key}[]`, item)
          }
        }
      }
    }

    const response = await axios.post('/bots', formData)

    if (!selectedBot.value) {
      selectBot(response.data)
    }

    selectableBots.value.push(response.data)

    return response.data
  }

  const update = async (id: number, payload: UpdateBotDTO): Promise<void> => {
    const formData = new FormData()

    for (const key in payload) {
      if (key === 'commands') {
        formData.append('commands', JSON.stringify(payload.commands ?? []))
      } else if (payload[key as keyof typeof payload] !== null && (payload[key as keyof typeof payload] !== '' || key === 'actionButtonLabel' || key === 'generatingPixMessage' || key === 'pixVerifyButtonLabel' || key === 'pixGeneratedInstruction' || key === 'pixPaymentInstruction') && !Array.isArray(payload[key as keyof typeof payload])) {
        formData.append(key, payload[key as keyof typeof payload] as string | File)
      } else if (Array.isArray(payload[key as keyof typeof payload])) {
        for (const item of payload[key as keyof typeof payload] as any[]) {
          if (item instanceof File) {
            formData.append(key, item)
          } else if (!!item) {
            formData.append(`${key}[]`, item)
          }
        }
      }
    }

    await axios.patch(`/bots/${id}`, formData)
  }

  const getAll = async (params?: GetAllBotsParams): Promise<Pagination<Bot>> => {
    if (params) {
      params = Object.fromEntries(
        Object.entries(params).filter(([param, value]) => !!value)
      )
    }

    if (selectedBot.value && (selectedBot.value?.organizationId != (organizationId ?? process.env.VUE_APP_ORGANIZATION_ID))) {
      localStorage.removeItem('selectedBot')
      selectedBot.value = null
      window.location.reload()
    }

    const response = await axios.get('/bots', { params })

    return response.data
  }

  const destroy = async (id: number): Promise<void> => {
    await axios.delete(`/bots/${id}`)
    if (selectedBot.value?.id == id) {
      localStorage.removeItem('selectedBot')
      selectedBot.value = null
      window.location.reload()
    }
  }

  const clone = async (id: number, token: string): Promise<Bot> => {
    const response = await axios.post(`/bots/${id}/clone`, { token })
    return response.data
  }

  const selectBot = (bot: Bot) => {
    selectedBot.value = bot
    localStorage.setItem('selectedBot', JSON.stringify(bot))
    window.location.reload()
  }

  return {
    create,
    getAll,
    update,
    destroy,
    clone,
    selectBot,
    selectedBot,
    selectableBots,
    botLink
  }
}